
import React, { createContext, useContext, useState, useEffect } from 'react';

// ==========================================
// CENTRALIZED THEME DEFINITIONS
// ==========================================

export type ThemeMode = 'light' | 'dark';

// --- NEW: KAI DYNAMIC PALETTE DEFINITION ---
export interface KaiThemePalette {
    name: string;
    primary: string;       // Main button / active state bg
    primaryHover: string;  // Hover state
    text: string;          // Accented text color
    bgLight: string;       // Very subtle background (bubbles, inputs)
    border: string;        // Border color
    ring: string;          // Focus rings
    shadow: string;        // Glow effects
    glass: string;         // Glassmorphism tint
    gradient: string;      // For text clips or backgrounds
}

export const KAI_THEMES: Record<string, { light: KaiThemePalette; dark: KaiThemePalette }> = {
    indigo: {
        light: {
            name: 'indigo',
            primary: 'bg-indigo-600 text-white',
            primaryHover: 'hover:bg-indigo-700',
            text: 'text-indigo-600',
            bgLight: 'bg-indigo-50',
            border: 'border-indigo-100',
            ring: 'ring-indigo-200',
            shadow: 'shadow-indigo-500/30',
            glass: 'bg-indigo-50/30',
            gradient: 'from-indigo-600 to-purple-600'
        },
        dark: {
            name: 'indigo',
            primary: 'bg-indigo-500 text-white',
            primaryHover: 'hover:bg-indigo-400',
            text: 'text-indigo-400',
            bgLight: 'bg-indigo-500/20',
            border: 'border-indigo-500/30',
            ring: 'ring-indigo-500/50',
            shadow: 'shadow-indigo-500/40',
            glass: 'bg-indigo-500/10',
            gradient: 'from-indigo-400 to-purple-400'
        }
    },
    rose: {
        light: {
            name: 'rose',
            primary: 'bg-rose-500 text-white',
            primaryHover: 'hover:bg-rose-600',
            text: 'text-rose-600',
            bgLight: 'bg-rose-50',
            border: 'border-rose-100',
            ring: 'ring-rose-200',
            shadow: 'shadow-rose-500/30',
            glass: 'bg-rose-50/30',
            gradient: 'from-rose-500 to-pink-600'
        },
        dark: {
            name: 'rose',
            primary: 'bg-rose-500 text-white',
            primaryHover: 'hover:bg-rose-400',
            text: 'text-rose-400',
            bgLight: 'bg-rose-500/20',
            border: 'border-rose-500/30',
            ring: 'ring-rose-500/50',
            shadow: 'shadow-rose-500/40',
            glass: 'bg-rose-500/10',
            gradient: 'from-rose-400 to-pink-400'
        }
    },
    emerald: {
        light: {
            name: 'emerald',
            primary: 'bg-emerald-500 text-white',
            primaryHover: 'hover:bg-emerald-600',
            text: 'text-emerald-600',
            bgLight: 'bg-emerald-50',
            border: 'border-emerald-100',
            ring: 'ring-emerald-200',
            shadow: 'shadow-emerald-500/30',
            glass: 'bg-emerald-50/30',
            gradient: 'from-emerald-500 to-teal-600'
        },
        dark: {
            name: 'emerald',
            primary: 'bg-emerald-500 text-white',
            primaryHover: 'hover:bg-emerald-400',
            text: 'text-emerald-400',
            bgLight: 'bg-emerald-500/20',
            border: 'border-emerald-500/30',
            ring: 'ring-emerald-500/50',
            shadow: 'shadow-emerald-500/40',
            glass: 'bg-emerald-500/10',
            gradient: 'from-emerald-400 to-teal-400'
        }
    },
    amber: {
        light: {
            name: 'amber',
            primary: 'bg-amber-500 text-white',
            primaryHover: 'hover:bg-amber-600',
            text: 'text-amber-600',
            bgLight: 'bg-amber-50',
            border: 'border-amber-100',
            ring: 'ring-amber-200',
            shadow: 'shadow-amber-500/30',
            glass: 'bg-amber-50/30',
            gradient: 'from-amber-500 to-orange-600'
        },
        dark: {
            name: 'amber',
            primary: 'bg-amber-500 text-white',
            primaryHover: 'hover:bg-amber-400',
            text: 'text-amber-400',
            bgLight: 'bg-amber-500/20',
            border: 'border-amber-500/30',
            ring: 'ring-amber-500/50',
            shadow: 'shadow-amber-500/40',
            glass: 'bg-amber-500/10',
            gradient: 'from-amber-400 to-orange-400'
        }
    }
};

export interface BucketStyle {
    bg: string;
    text: string;
    border: string;
    icon: string;
    indicator: string;
    pulse: string;
}

export interface FeatureColor {
    bg: string;
    text: string;
    border: string;
    icon: string;
    button: string;
}

export interface ChatModeStyle {
    container: string;
    bgEffect: string;
    header: string;
    text: string;
    accent: string;
    userBubble: string;
    userIcon: string;
    aiBubble: string;
    inputContainer: string;
    input: string;
    primaryButton: string;
    iconBg: string;
}

export interface ThemeColors {
    bg: string;
    surface: string;
    surfaceHighlight: string;
    text: {
        main: string;
        secondary: string;
        accent: string;
        inverse: string;
    };
    border: string;
    divider: string;
    input: {
        bg: string;
        border: string;
        text: string;
        placeholder: string;
        focusRing: string;
    };
    status: {
        success: string;
        warning: string;
        error: string;
        info: string;
    };
    primary: string; 
    primaryBg: string;
    buckets: {
        passion: BucketStyle;
        mission: BucketStyle;
        profession: BucketStyle;
        vocation: BucketStyle;
    };
    features: {
        simulator: FeatureColor;
        mapper: FeatureColor;
        bridger: FeatureColor;
        manifesto: FeatureColor;
        debater: FeatureColor;
        mirror: FeatureColor;
        auction: FeatureColor;
        vision: FeatureColor;
        skillTree: FeatureColor;
    };
    chat: {
        standard: ChatModeStyle;
        timeCapsule: ChatModeStyle;
        originStory: ChatModeStyle;
    }
}

export interface ComponentStyles {
    card: string;
    holographicCard: string;
    button: {
        primary: string;
        secondary: string;
        icon: string;
        danger: string;
    };
    sidebar: {
        container: string;
        itemActive: string;
        itemInactive: string;
        iconPrimary: string;
        iconSecondary: string;
        logo: string;
        kaiToggleActive: string;
        profileActiveRing: string;
        logoutHover: string;
        themeToggle: string;
    };
    modal: {
        overlay: string;
        container: string;
        header: string;
        body: string;
        footer: string;
    };
}

export interface Theme {
    mode: ThemeMode;
    colors: ThemeColors;
    components: ComponentStyles;
    utils: {
        shadow: string;
        radius: string; 
    }
}

// --- LIGHT THEME ---
const lightTheme: Theme = {
    mode: 'light',
    utils: {
        shadow: 'shadow-[0_8px_30px_rgb(0,0,0,0.04)]',
        radius: 'rounded-3xl'
    },
    colors: {
        bg: 'bg-[#F0F2F5]',
        surface: 'bg-white',
        surfaceHighlight: 'bg-white/50',
        text: {
            main: 'text-slate-800',
            secondary: 'text-slate-500',
            accent: 'text-indigo-600',
            inverse: 'text-white'
        },
        border: 'border-slate-200',
        divider: 'border-slate-200/60',
        input: {
            bg: 'bg-white/80',
            border: 'border-white',
            text: 'text-slate-900',
            placeholder: 'placeholder:text-slate-400',
            focusRing: 'focus:ring-2 focus:ring-indigo-500/20'
        },
        status: {
            success: 'text-green-600 bg-green-50 border-green-200',
            warning: 'text-amber-600 bg-amber-50 border-amber-200',
            error: 'text-red-600 bg-red-50 border-red-200',
            info: 'text-blue-600 bg-blue-50 border-blue-200'
        },
        primary: 'text-indigo-600',
        primaryBg: 'bg-indigo-600',
        buckets: {
            passion: { bg: 'bg-rose-50/50', text: 'text-rose-900', border: 'border-rose-100', icon: 'text-rose-500', indicator: 'bg-rose-500', pulse: 'shadow-[0_0_0_4px_rgba(244,63,94,0.1)]' },
            mission: { bg: 'bg-purple-50/50', text: 'text-purple-900', border: 'border-purple-100', icon: 'text-purple-500', indicator: 'bg-purple-500', pulse: 'shadow-[0_0_0_4px_rgba(168,85,247,0.1)]' },
            profession: { bg: 'bg-sky-50/50', text: 'text-sky-900', border: 'border-sky-100', icon: 'text-sky-500', indicator: 'bg-sky-500', pulse: 'shadow-[0_0_0_4px_rgba(14,165,233,0.1)]' },
            vocation: { bg: 'bg-emerald-50/50', text: 'text-emerald-900', border: 'border-emerald-100', icon: 'text-emerald-500', indicator: 'bg-emerald-500', pulse: 'shadow-[0_0_0_4px_rgba(16,185,129,0.1)]' }
        },
        features: {
            simulator: { 
                bg: 'bg-indigo-600', text: 'text-white', border: 'border-indigo-100', icon: 'text-indigo-600',
                button: 'text-indigo-700 bg-indigo-50 border-indigo-100 hover:bg-indigo-100 hover:border-indigo-200'
            },
            mapper: { 
                bg: 'bg-blue-600', text: 'text-white', border: 'border-blue-100', icon: 'text-blue-600',
                button: 'text-blue-700 bg-blue-50 border-blue-100 hover:bg-blue-100 hover:border-blue-200'
            },
            bridger: { 
                bg: 'bg-emerald-600', text: 'text-white', border: 'border-emerald-100', icon: 'text-emerald-600',
                button: 'text-emerald-700 bg-emerald-50 border-emerald-100 hover:bg-emerald-100 hover:border-emerald-200'
            },
            manifesto: { 
                bg: 'bg-orange-500', text: 'text-white', border: 'border-orange-100', icon: 'text-orange-500',
                button: 'text-orange-700 bg-orange-50 border-orange-100 hover:bg-orange-100 hover:border-orange-200'
            },
            debater: { 
                bg: 'bg-indigo-500', text: 'text-white', border: 'border-indigo-100', icon: 'text-indigo-500',
                button: 'text-indigo-700 bg-indigo-50 border-indigo-100 hover:bg-indigo-100 hover:border-indigo-200'
            },
            mirror: { 
                bg: 'bg-cyan-500', text: 'text-white', border: 'border-cyan-100', icon: 'text-cyan-500',
                button: 'text-cyan-700 bg-cyan-50 border-cyan-100 hover:bg-cyan-100 hover:border-cyan-200'
            },
            auction: { 
                bg: 'bg-amber-500', text: 'text-white', border: 'border-amber-100', icon: 'text-amber-500',
                button: 'text-amber-700 bg-amber-50 border-amber-100 hover:bg-amber-100 hover:border-amber-200'
            },
            vision: {
                bg: 'bg-pink-500', text: 'text-white', border: 'border-pink-100', icon: 'text-pink-500',
                button: 'text-pink-700 bg-pink-50 border-pink-100 hover:bg-pink-100 hover:border-pink-200'
            },
            skillTree: {
                bg: 'bg-purple-500', text: 'text-white', border: 'border-purple-100', icon: 'text-purple-500',
                button: 'text-purple-700 bg-purple-50 border-purple-100 hover:bg-purple-100 hover:border-purple-200'
            }
        },
        chat: {
            standard: {
                container: 'bg-white/60 backdrop-blur-xl border border-white/40 shadow-sm',
                bgEffect: 'bg-gradient-to-b from-white/40 to-white/10',
                header: 'bg-gradient-to-r from-white/90 via-indigo-50/30 to-white/90 backdrop-blur-md border-b border-indigo-50', 
                text: 'text-slate-800',
                accent: 'text-indigo-600',
                userBubble: 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-lg shadow-indigo-500/20 rounded-[24px_4px_24px_24px]',
                userIcon: 'bg-indigo-50 text-indigo-600',
                aiBubble: 'bg-white text-slate-700 shadow-sm border border-slate-100 rounded-[4px_24px_24px_24px]',
                inputContainer: 'bg-white/80 backdrop-blur-xl border border-indigo-50 shadow-[0_8px_32px_rgba(0,0,0,0.08)]',
                input: 'bg-transparent border-none text-slate-800 focus:ring-0 placeholder:text-slate-400',
                primaryButton: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-200',
                iconBg: 'bg-indigo-600'
            },
            timeCapsule: {
                container: 'bg-slate-50/50 backdrop-blur-xl border border-white/40',
                bgEffect: 'bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-violet-100/40 via-transparent to-transparent',
                header: 'bg-white/60 backdrop-blur-md border-b border-white/50',
                text: 'text-slate-800',
                accent: 'text-violet-600',
                userBubble: 'bg-white text-slate-800 border border-indigo-100 shadow-sm rounded-[24px_4px_24px_24px]',
                userIcon: 'bg-violet-100 text-violet-700',
                aiBubble: 'bg-gradient-to-br from-violet-50 to-white text-slate-800 border border-violet-100 shadow-sm rounded-[4px_24px_24px_24px]',
                inputContainer: 'bg-white/90 backdrop-blur-xl border border-white/50 shadow-[0_0_40px_rgba(139,92,246,0.1)]',
                input: 'bg-transparent border-none text-slate-800 focus:ring-0',
                primaryButton: 'bg-violet-600 hover:bg-violet-500 text-white shadow-lg shadow-violet-200',
                iconBg: 'bg-violet-600'
            },
            originStory: {
                container: 'bg-orange-50/30 backdrop-blur-xl border border-white/40',
                bgEffect: 'bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-orange-100/40 via-transparent to-transparent',
                header: 'bg-white/60 backdrop-blur-md border-b border-white/50',
                text: 'text-slate-800',
                accent: 'text-orange-600',
                userBubble: 'bg-white text-slate-800 border border-orange-100 shadow-sm rounded-[24px_4px_24px_24px]',
                userIcon: 'bg-orange-100 text-orange-700',
                aiBubble: 'bg-gradient-to-br from-orange-50 to-white text-slate-800 border border-orange-100 shadow-sm rounded-[4px_24px_24px_24px]',
                inputContainer: 'bg-white/90 backdrop-blur-xl border border-white/50 shadow-[0_0_40px_rgba(249,115,22,0.1)]',
                input: 'bg-transparent border-none text-slate-800 focus:ring-0 placeholder:text-stone-600',
                primaryButton: 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-200',
                iconBg: 'bg-orange-500'
            }
        }
    },
    components: {
        card: 'bg-white border border-slate-100 shadow-sm rounded-[32px]',
        holographicCard: 'bg-white/40 backdrop-blur-md border border-white/60 shadow-lg rounded-[32px] hover:shadow-2xl hover:bg-white/60 hover:border-white/20 transition-all duration-500',
        button: {
            primary: 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-md shadow-indigo-900/20 rounded-xl transition-all',
            secondary: 'bg-white text-slate-600 border border-slate-200 shadow-sm hover:shadow-md hover:text-indigo-600 hover:border-indigo-100 rounded-2xl transition-all',
            icon: 'text-slate-400 hover:text-indigo-600 hover:bg-indigo-50/50 rounded-xl transition-all',
            danger: 'bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 rounded-xl transition-all'
        },
        sidebar: {
            container: 'bg-white/80 backdrop-blur-2xl border border-white/60 shadow-[0_8px_40px_rgb(0,0,0,0.06)] rounded-[40px] z-30',
            itemActive: 'bg-white shadow-[0_4px_20px_rgba(99,102,241,0.15)] text-indigo-600 rounded-2xl ring-1 ring-indigo-50',
            itemInactive: 'text-slate-400 hover:text-indigo-500 hover:bg-white/50 rounded-2xl',
            iconPrimary: 'text-indigo-600 hover:bg-indigo-50',
            iconSecondary: 'text-slate-400 hover:bg-slate-50 hover:text-indigo-600',
            logo: 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-xl shadow-indigo-200',
            kaiToggleActive: 'text-indigo-300 bg-indigo-500/20 shadow-inner',
            profileActiveRing: 'ring-2 ring-offset-2 ring-offset-slate-900 ring-indigo-500 border-transparent shadow-md scale-105',
            logoutHover: 'hover:text-red-400 hover:bg-red-900/20',
            themeToggle: 'text-amber-400 hover:bg-amber-900/20 hover:text-amber-300' 
        },
        modal: {
            overlay: 'bg-slate-900/20 backdrop-blur-md',
            container: 'bg-white/90 backdrop-blur-xl rounded-[32px] shadow-2xl border border-white/50',
            header: 'bg-white/50 border-b border-slate-100',
            body: 'bg-white/50 text-slate-800',
            footer: 'bg-white/50 border-t border-slate-100'
        }
    }
};

// --- DARK THEME ---
const darkTheme: Theme = {
    mode: 'dark',
    utils: {
        shadow: 'shadow-lg shadow-black/40',
        radius: 'rounded-2xl'
    },
    colors: {
        bg: 'bg-[#05050A]', // Deepest Space
        surface: 'bg-[#0f172a]/40', // Slate 900 Glass
        surfaceHighlight: 'bg-[#1e293b]/60', // Slate 800 Glass
        text: {
            main: 'text-slate-100',
            secondary: 'text-slate-400',
            accent: 'text-indigo-400',
            inverse: 'text-slate-900'
        },
        border: 'border-white/10',
        divider: 'border-white/5',
        input: {
            bg: 'bg-[#0B0F17]',
            border: 'border-slate-800',
            text: 'text-slate-100',
            placeholder: 'placeholder:text-slate-600',
            focusRing: 'focus:ring-indigo-500'
        },
        status: {
            success: 'text-green-300 bg-green-900/30 border-green-500/20',
            warning: 'text-amber-300 bg-amber-900/30 border-amber-500/20',
            error: 'text-red-300 bg-red-900/30 border-red-500/20',
            info: 'text-blue-300 bg-blue-900/30 border-blue-500/20'
        },
        primary: 'text-indigo-400',
        primaryBg: 'bg-indigo-500', 
        buckets: {
            passion: { bg: 'bg-rose-950/30', text: 'text-rose-100', border: 'border-rose-500/20', icon: 'text-rose-400', indicator: 'bg-rose-500', pulse: 'shadow-[0_0_0_4px_rgba(244,63,94,0.1)]' },
            mission: { bg: 'bg-purple-950/30', text: 'text-purple-100', border: 'border-purple-500/20', icon: 'text-purple-400', indicator: 'bg-purple-500', pulse: 'shadow-[0_0_0_4px_rgba(168,85,247,0.1)]' },
            profession: { bg: 'bg-sky-950/30', text: 'text-sky-100', border: 'border-sky-500/20', icon: 'text-sky-400', indicator: 'bg-sky-500', pulse: 'shadow-[0_0_0_4px_rgba(14,165,233,0.1)]' },
            vocation: { bg: 'bg-emerald-950/30', text: 'text-emerald-100', border: 'border-emerald-500/20', icon: 'text-emerald-400', indicator: 'bg-emerald-500', pulse: 'shadow-[0_0_0_4px_rgba(16,185,129,0.1)]' }
        },
        features: {
            simulator: { 
                bg: 'bg-indigo-500/10', text: 'text-indigo-200', border: 'border-indigo-500/20', icon: 'text-indigo-400',
                button: 'text-indigo-100 bg-indigo-500/20 border-indigo-500/30 hover:bg-indigo-500/30'
            },
            mapper: { 
                bg: 'bg-blue-500/10', text: 'text-blue-200', border: 'border-blue-500/20', icon: 'text-blue-400',
                button: 'text-blue-100 bg-blue-500/20 border-blue-500/30 hover:bg-blue-500/30'
            },
            bridger: { 
                bg: 'bg-emerald-500/10', text: 'text-emerald-200', border: 'border-emerald-500/20', icon: 'text-emerald-400',
                button: 'text-emerald-100 bg-emerald-500/20 border-emerald-500/30 hover:bg-emerald-500/30'
            },
            manifesto: { 
                bg: 'bg-orange-500/10', text: 'text-orange-200', border: 'border-orange-500/20', icon: 'text-orange-400',
                button: 'text-orange-100 bg-orange-500/20 border-orange-500/30 hover:bg-orange-500/30'
            },
            debater: { 
                bg: 'bg-indigo-500/10', text: 'text-indigo-200', border: 'border-indigo-500/20', icon: 'text-indigo-400',
                button: 'text-indigo-100 bg-indigo-500/20 border-indigo-500/30 hover:bg-indigo-500/30'
            },
            mirror: { 
                bg: 'bg-cyan-500/10', text: 'text-cyan-200', border: 'border-cyan-500/20', icon: 'text-cyan-400',
                button: 'text-cyan-100 bg-cyan-500/20 border-cyan-500/30 hover:bg-cyan-500/30'
            },
            auction: { 
                bg: 'bg-amber-500/10', text: 'text-amber-200', border: 'border-amber-500/20', icon: 'text-amber-400',
                button: 'text-amber-100 bg-amber-500/20 border-amber-500/30 hover:bg-amber-500/30'
            },
            vision: {
                bg: 'bg-pink-500/10', text: 'text-pink-200', border: 'border-pink-500/20', icon: 'text-pink-400',
                button: 'text-pink-100 bg-pink-500/20 border-pink-500/30 hover:bg-pink-500/30'
            },
            skillTree: {
                bg: 'bg-purple-500/10', text: 'text-purple-200', border: 'border-purple-500/20', icon: 'text-purple-400',
                button: 'text-purple-100 bg-purple-500/20 border-purple-500/30 hover:bg-purple-500/30'
            }
        },
        chat: {
            standard: {
                container: 'bg-[#0f172a]/60 backdrop-blur-2xl border border-white/5 shadow-2xl',
                bgEffect: 'bg-gradient-to-b from-white/5 to-transparent',
                header: 'bg-[#0f172a]/80 backdrop-blur-xl border-b border-white/5', 
                text: 'text-slate-100',
                accent: 'text-indigo-400',
                userBubble: 'bg-indigo-600 text-white rounded-[24px_4px_24px_24px]',
                userIcon: 'bg-slate-800 text-slate-300',
                aiBubble: 'bg-[#1e293b]/80 text-slate-200 border border-white/5 rounded-[4px_24px_24px_24px]',
                inputContainer: 'bg-[#0f172a]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/50',
                input: 'bg-transparent border-none text-white focus:ring-0 placeholder:text-slate-500',
                primaryButton: 'bg-indigo-600 hover:bg-indigo-500 text-white',
                iconBg: 'bg-indigo-500'
            },
            timeCapsule: {
                container: 'bg-[#020617]/80 backdrop-blur-2xl border border-violet-500/10',
                bgEffect: 'bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-violet-900/20 via-transparent to-transparent',
                header: 'bg-[#0f172a]/80 border-b border-violet-500/10',
                text: 'text-violet-100',
                accent: 'text-violet-400',
                userBubble: 'bg-[#1e293b] text-white border border-white/5 rounded-[24px_4px_24px_24px]',
                userIcon: 'bg-slate-700 text-slate-300',
                aiBubble: 'bg-[#020617] text-violet-100 border border-violet-500/20 shadow-[0_0_20px_rgba(139,92,246,0.05)] rounded-[4px_24px_24px_24px]',
                inputContainer: 'bg-[#020617]/80 backdrop-blur-xl border border-violet-500/20',
                input: 'bg-transparent border-none text-white focus:ring-0 placeholder:text-violet-700/50',
                primaryButton: 'bg-violet-600 hover:bg-violet-500 text-white shadow-[0_0_10px_rgba(124,58,237,0.4)]',
                iconBg: 'bg-violet-600'
            },
            originStory: {
                container: 'bg-[#0c0a09]/80 backdrop-blur-2xl border border-orange-500/10',
                bgEffect: 'bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-orange-900/20 via-transparent to-transparent',
                header: 'bg-[#0f172a]/80 border-b border-orange-500/10',
                text: 'text-stone-200',
                accent: 'text-orange-500',
                userBubble: 'bg-[#1c1917] text-stone-200 border border-white/5 rounded-[24px_4px_24px_24px]',
                userIcon: 'bg-stone-800 text-stone-300',
                aiBubble: 'bg-[#0c0a09] text-orange-100 border border-orange-500/20 rounded-[4px_24px_24px_24px]',
                inputContainer: 'bg-[#0c0a09]/80 backdrop-blur-xl border border-orange-500/20',
                input: 'bg-transparent border-none text-stone-200 focus:ring-0 placeholder:text-stone-600',
                primaryButton: 'bg-orange-700 hover:bg-orange-600 text-orange-50',
                iconBg: 'bg-orange-700'
            }
        }
    },
    components: {
        card: 'bg-[#0f172a]/40 backdrop-blur-md border border-white/10 shadow-xl rounded-[32px]',
        holographicCard: 'bg-[#0f172a]/40 backdrop-blur-md border border-white/20 shadow-lg rounded-[32px] hover:shadow-2xl hover:bg-[#0f172a]/60 hover:border-white/30 transition-all duration-500',
        button: {
            primary: 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-md shadow-indigo-900/20 rounded-xl transition-all',
            secondary: 'bg-[#1e293b]/60 text-slate-300 border border-white/10 hover:bg-[#1e293b] rounded-xl transition-all',
            icon: 'text-slate-500 hover:text-indigo-400 hover:bg-white/5 rounded-xl transition-all',
            danger: 'bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-xl transition-all'
        },
        sidebar: {
            container: 'bg-[#0f172a]/80 backdrop-blur-2xl border border-white/5 shadow-2xl rounded-[40px] z-30',
            itemActive: 'bg-white/10 text-white shadow-lg shadow-black/20 rounded-2xl ring-1 ring-white/10',
            itemInactive: 'text-slate-400 hover:bg-white/5 hover:text-indigo-300 rounded-2xl',
            iconPrimary: 'text-indigo-400 hover:bg-white/5',
            iconSecondary: 'text-slate-400 hover:bg-white/5 hover:text-indigo-300', 
            logo: 'bg-indigo-500 text-white shadow-lg shadow-indigo-900/40',
            kaiToggleActive: 'text-indigo-300 bg-indigo-500/20 shadow-inner',
            profileActiveRing: 'ring-2 ring-offset-2 ring-offset-slate-900 ring-indigo-500 border-transparent shadow-md scale-105',
            logoutHover: 'hover:text-red-400 hover:bg-red-500/20',
            themeToggle: 'text-amber-400 hover:bg-amber-500/20 hover:text-amber-300' 
        },
        modal: {
            overlay: 'bg-black/90 backdrop-blur-lg',
            container: 'bg-[#0f172a]/90 backdrop-blur-2xl rounded-[32px] shadow-2xl border border-white/10',
            header: 'bg-[#0f172a]/50 border-b border-white/5',
            body: 'bg-transparent text-slate-200',
            footer: 'bg-[#0f172a]/50 border-t border-white/5'
        }
    }
};

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
    setTheme: (mode: ThemeMode) => void;
    getKaiTheme: (color: string) => KaiThemePalette; // Export the helper
}

const ThemeContext = createContext<ThemeContextType>({
    theme: lightTheme,
    toggleTheme: () => {},
    setTheme: () => {},
    getKaiTheme: () => KAI_THEMES['indigo'].light // Default fallback
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mode, setMode] = useState<ThemeMode>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('kai_theme');
            return saved === 'dark' ? 'dark' : 'light';
        }
        return 'light';
    });

    const toggleTheme = () => {
        const newMode = mode === 'light' ? 'dark' : 'light';
        setMode(newMode);
    };

    const setTheme = (newMode: ThemeMode) => {
        setMode(newMode);
    };

    const activeTheme = mode === 'light' ? lightTheme : darkTheme;

    // Helper to get dynamic Kai Theme
    const getKaiTheme = (color: string): KaiThemePalette => {
        const key = color.toLowerCase();
        const set = KAI_THEMES[key] || KAI_THEMES['indigo'];
        return mode === 'light' ? set.light : set.dark;
    };

    useEffect(() => {
        if (mode === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
        localStorage.setItem('kai_theme', mode);
    }, [mode]);

    return (
        <ThemeContext.Provider value={{ theme: activeTheme, toggleTheme, setTheme, getKaiTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
